import type {
  FindingStatus,
  ReviewStatus,
  ReviewWorkflowState,
  Severity,
} from "./types";

export const SEVERITY_LABEL: Record<Severity, string> = {
  blocking: "Blokująca",
  major: "Istotna",
  minor: "Drobna",
  to_clarify: "Do wyjaśnienia",
};

export const SEVERITY_COLOR: Record<Severity, "red" | "orange" | "gray" | "yellow"> = {
  blocking: "red",
  major: "orange",
  minor: "gray",
  to_clarify: "yellow",
};

export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  uploaded: "Wgrany",
  queued: "W kolejce",
  running: "Analiza w toku",
  done: "Zakończona",
  done_partial: "Zakończona częściowo",
  skipped: "Bez analizy AI",
  error: "Błąd",
  cancelled: "Anulowana",
};

export const REVIEW_STATUS_COLOR: Record<
  ReviewStatus,
  "gray" | "blue" | "yellow" | "green" | "orange" | "red"
> = {
  uploaded: "gray",
  queued: "blue",
  running: "yellow",
  done: "green",
  done_partial: "orange",
  skipped: "blue",
  error: "red",
  cancelled: "gray",
};

// Krótkie objaśnienia statusów analizy AI — pokazywane w dymku po najechaniu na
// odznakę statusu, żeby użytkownik wiedział, co dany stan oznacza.
export const REVIEW_STATUS_DESC: Record<ReviewStatus, string> = {
  uploaded: "Dokument wgrany, analiza AI jeszcze nie ruszyła. Kliknij „Analizuj”, aby ją uruchomić.",
  queued: "Dokument czeka w kolejce na analizę AI — ruszy automatycznie, gdy zwolni się miejsce.",
  running: "AI analizuje dokument w tej chwili — sprawdza tekst, mapy i spójność danych.",
  done: "Analiza AI zakończona w całości — wszystkie etapy przeszły, uwagi są kompletne.",
  done_partial:
    "Analiza AI zakończona, ale część etapów nie przeszła (np. zbyt trudne mapy) — uwagi mogą być niepełne.",
  skipped: "Recenzja prowadzona ręcznie, bez analizy AI — uwagi dodaje recenzent samodzielnie.",
  error: "Analiza AI przerwana błędem. Kliknij „Analizuj”, aby spróbować ponownie.",
  cancelled: "Analiza AI została anulowana i nie została ukończona.",
};

export const FINDING_STATUS_LABEL: Record<FindingStatus, string> = {
  open: "Otwarta",
  confirmed: "Potwierdzona",
  dismissed: "Odrzucona",
  fixed: "Poprawiona",
};

export const CATEGORY_LABEL: Record<string, string> = {
  terminology: "Terminologia",
  maps: "Mapy i rysunki",
  doc_structure: "Struktura",
  attachments: "Załączniki",
  consistency: "Spójność danych",
  process: "Proces",
  billing: "Rozliczenia",
  other: "Inne",
};

export const REVIEW_MODES: {
  value: "economy" | "standard" | "thorough";
  label: string;
  cost: string;
  hint: string;
}[] = [
  {
    value: "economy",
    label: "Ekonomiczny",
    cost: "$",
    hint: "Bez modelu Opus — najtaniej, słabszy na trudnych mapach i spójności liczb.",
  },
  {
    value: "standard",
    label: "Standardowy",
    cost: "$$",
    hint: "Domyślny — Opus do map i spójności, reszta tańsza. Dobra równowaga.",
  },
  {
    value: "thorough",
    label: "Dokładny",
    cost: "$$$",
    hint: "Najwyższy effort na mapach i spójności — najdokładniej, najdrożej.",
  },
];

export const WORKFLOW_STATE_LABEL: Record<ReviewWorkflowState, string> = {
  draft: "U autora",
  in_review: "W recenzji",
  returned: "Zwrócona do poprawy",
  accepted: "Zaakceptowana",
  accepted_conditional: "Zaakceptowana warunkowo",
};

export const WORKFLOW_STATE_COLOR: Record<
  ReviewWorkflowState,
  "gray" | "blue" | "yellow" | "green" | "orange" | "red"
> = {
  draft: "gray",
  in_review: "blue",
  returned: "orange",
  accepted: "green",
  accepted_conditional: "yellow",
};

// Krótkie objaśnienia stanu obiegu recenzji — kto aktualnie „trzyma” opracowanie.
export const WORKFLOW_STATE_DESC: Record<ReviewWorkflowState, string> = {
  draft: "Opracowanie jest u autora (projektanta) — nie zostało jeszcze przekazane do recenzji.",
  in_review: "Opracowanie jest w trakcie sprawdzania przez recenzenta.",
  returned: "Recenzent zwrócił opracowanie autorowi do poprawy — czeka na naniesienie zmian.",
  accepted: "Recenzent zaakceptował opracowanie bez zastrzeżeń.",
  accepted_conditional:
    "Recenzent zaakceptował opracowanie warunkowo — z uwagami do uwzględnienia.",
};

// ── Szacowany, skokowy pasek postępu recenzji ────────────────────────────────
// Udział każdego etapu w całości CZASU (nie liczby etapów) — dostrojony „na oko"
// do pipeline'u 7-etapowego: przebiegi na Opusie z wizją (mapy, spójność) trwają
// najdłużej, tekst/inwentaryzacja/weryfikacja są tanie. Każdy etap ma pasmo
// [start, koniec] w %; pasek skacze na start pasma, gdy etap się zaczyna, a w
// etapach wsadowych (render/inwentaryzacja/mapy) płynnie dobiega do końca pasma
// wg pod-postępu „strona/partia X/Y". Zmiana kolejności/liczby etapów w
// reviewer.py wymaga aktualizacji tej tabeli.
const STAGE_BANDS: Record<string, [number, number]> = {
  prep: [0, 8], //  render PDF (poppler)
  etap1: [8, 14], //  inwentaryzacja stron (haiku)
  etap2: [14, 24], //  przegląd tekstu (sonnet)
  etap3: [24, 54], //  analiza map i rysunków (opus + wizja) — najcięższy
  etap4: [54, 63], //  kompletność załączników (sonnet)
  etap5: [63, 83], //  spójność danych (opus, wysoki effort)
  etap6: [83, 90], //  przebieg trasy i zakres vs uzgodnienia (read_map per mapa)
  mapq: [90, 93], //  ocena czytelności map
  etap7: [93, 100], // weryfikacja + agregacja
};

export interface ReviewProgress {
  pct: number;
  label: string;
}

/** Odczytuje z logu bieżący etap i zwraca skokowy % + etykietę. */
export function reviewProgress(log: string, status: ReviewStatus): ReviewProgress | null {
  if (status === "queued") return { pct: 2, label: "W kolejce…" };
  if (status !== "running") return null;

  const lines = (log || "").split("\n");
  let key = "prep";
  let label = "Przygotowanie";
  let markerIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    if (/^Przygotowanie:/.test(line)) {
      key = "prep";
      label = "Przygotowanie";
      markerIdx = i;
      break;
    }
    const m = line.match(/^Etap (\d+)\/(\d+):\s*(.*)$/);
    if (m) {
      key = `etap${m[1]}`;
      label = `Etap ${m[1]}/${m[2]} · ${m[3].replace(/…\s*$/, "")}`;
      markerIdx = i;
      break;
    }
    if (/^Ocena czytelności map/.test(line)) {
      key = "mapq";
      label = "Ocena czytelności map";
      markerIdx = i;
      break;
    }
  }

  const band = STAGE_BANDS[key] ?? [0, 100];
  let pct = band[0];
  // Płynne dobieganie w obrębie etapu wsadowego wg ostatniego „strona/partia X/Y".
  if (markerIdx >= 0) {
    for (let i = lines.length - 1; i > markerIdx; i--) {
      const sub = lines[i].match(/(?:strona|partia)\s+(\d+)\s*\/\s*(\d+)/);
      if (sub) {
        const x = Number(sub[1]);
        const y = Number(sub[2]);
        if (y > 0) pct = band[0] + Math.min(1, x / y) * (band[1] - band[0]);
        break;
      }
    }
  }
  return { pct: Math.round(Math.max(0, Math.min(100, pct))), label };
}

export const DOC_TYPE_LABEL: Record<string, string> = {
  pb: "Projekt budowlany",
  pw: "Projekt wykonawczy",
  wniosek: "Wniosek",
  zud: "Dokumentacja ZUD",
  inne: "Inne opracowanie",
};
